<template>
  <div class="category-attribute-page">
    <el-card class="attribute-card" shadow="always">
      <div class="attribute-card-header">
        <span>分类属性配置</span>
        <div class="header-actions">
          <el-button type="primary" @click="openConfigDialog">新增属性配置</el-button>
        </div>
      </div>
      
      <!-- 分类选择器 -->
      <div class="category-selector">
        <el-cascader
          v-model="selectedCategoryPath"
          :options="categoryOptions"
          :props="cascaderProps"
          placeholder="请选择分类"
          clearable
          @change="handleCategoryChange"
          style="width: 300px;"
        />
      </div>

      <!-- 属性配置列表 -->
      <div class="attribute-list" v-if="selectedCategoryId">
        <el-table :data="attributeList" style="width: 100%">
          <el-table-column prop="attributeName" label="属性名称" width="150" />
          <el-table-column prop="attributeType" label="属性类型" width="120">
            <template #default="scope">
              <el-tag :type="getAttributeTypeTag(scope.row.attributeType)">
                {{ getAttributeTypeLabel(scope.row.attributeType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="isRequired" label="是否必填" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.isRequired ? 'danger' : 'info'">
                {{ scope.row.isRequired ? '必填' : '选填' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="sortOrder" label="排序" width="80" />
          <el-table-column prop="defaultValue" label="默认值" width="120" />
          <el-table-column prop="placeholder" label="提示信息" />
          <el-table-column prop="status" label="状态" width="80">
            <template #default="scope">
              <el-tag :type="scope.row.status ? 'success' : 'warning'">
                {{ scope.row.status ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="148" class-name="op-col">
            <template #default="scope">
              <div class="op-cell">
                <el-button type="primary" link size="small" @click="handleEditConfig(scope.row)">编辑</el-button>
                <el-button type="primary" link size="small" @click="handleOptionManage(scope.row)">选项管理</el-button>
                <el-button
                  :type="scope.row.status === 1 ? 'danger' : 'success'"
                  link
                  size="small"
                  @click="handleToggleStatus(scope.row)"
                >{{ scope.row.status === 1 ? '停用' : '启用' }}</el-button>
                <el-button type="danger" link size="small" @click="handleDeleteConfig(scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 无数据提示 -->
      <div v-else-if="!selectedCategoryId" class="no-data">
        <el-empty description="请先选择分类" />
      </div>

      <div v-else class="no-data">
        <el-empty description="暂无属性配置" />
      </div>
    </el-card>

    <!-- 属性配置对话框 -->
    <el-dialog :title="configDialogTitle" v-model="configDialogVisible" width="600px">
      <el-form :model="configForm" :rules="configRules" ref="configFormRef" label-width="100px">
        <el-form-item label="属性名称" prop="attributeName">
          <el-input v-model="configForm.attributeName" placeholder="请输入属性名称" />
        </el-form-item>
        <el-form-item label="属性类型" prop="attributeType">
          <el-select v-model="configForm.attributeType" placeholder="请选择属性类型" style="width: 100%">
            <el-option label="文本输入" value="text" />
            <el-option label="下拉选择" value="select" />
            <el-option label="复选框" value="checkbox" />
            <el-option label="数字输入" value="number" />
            <el-option label="日期选择" value="date" />
          </el-select>
        </el-form-item>
        <el-form-item label="输入单位" prop="inputUnit">
          <el-input v-model="configForm.inputUnit" placeholder="如：kg、cm、元等" />
        </el-form-item>
        <el-form-item label="是否必填">
          <el-switch v-model="configForm.isRequired" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="排序顺序">
          <el-input-number v-model="configForm.sortOrder" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="默认值">
          <el-input v-model="configForm.defaultValue" placeholder="默认值（可选）" />
        </el-form-item>
        <el-form-item label="提示信息">
          <el-input v-model="configForm.placeholder" placeholder="输入提示信息" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="configForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="configDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmConfig">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

// 数据
const selectedCategoryPath = ref([])
const selectedCategoryId = ref(null)
const categoryOptions = ref([])
const attributeList = ref([])
const configDialogVisible = ref(false)
const configForm = ref({
  attributeName: '',
  attributeType: 'text',
  inputUnit: '',
  isRequired: 0,
  sortOrder: 0,
  defaultValue: '',
  placeholder: '',
  status: 1
})
const configFormRef = ref()
const editingConfig = ref(null)

// 级联选择器配置
const cascaderProps = {
  value: 'id',
  label: 'name',
  children: 'children',
  checkStrictly: true,
  emitPath: false
}

// 表单验证规则
const configRules = {
  attributeName: [
    { required: true, message: '请输入属性名称', trigger: 'blur' }
  ],
  attributeType: [
    { required: true, message: '请选择属性类型', trigger: 'change' }
  ]
}

// 计算属性
const configDialogTitle = computed(() => {
  return editingConfig.value ? '编辑属性配置' : '新增属性配置'
})

// 方法
const loadCategories = async () => {
  try {
    const res = await axios.get('/api/categories/list', { params: { size: 999 } })
    const categories = res.data?.data?.records || []
    categoryOptions.value = buildCategoryTree(categories)
  } catch (error) {
    ElMessage.error('加载分类失败')
  }
}

const buildCategoryTree = (categories) => {
  const tree = []
  const map = {}
  
  // 创建映射
  categories.forEach(item => {
    map[item.id] = { ...item, children: [] }
  })
  
  // 构建树形结构
  categories.forEach(item => {
    if (item.pid === 0) {
      tree.push(map[item.id])
    } else if (map[item.pid]) {
      map[item.pid].children.push(map[item.id])
    }
  })
  
  return tree
}

const handleCategoryChange = (categoryId) => {
  selectedCategoryId.value = categoryId
  if (categoryId) {
    loadAttributeConfigs(categoryId)
  } else {
    attributeList.value = []
  }
}

const loadAttributeConfigs = async (categoryId) => {
  try {
    const res = await axios.get('/api/meta-category-attribute-config/by-category/' + categoryId)
    attributeList.value = res.data?.data || []
  } catch (error) {
    ElMessage.error('加载属性配置失败')
  }
}

const openConfigDialog = () => {
  if (!selectedCategoryId.value) {
    ElMessage.warning('请先选择分类')
    return
  }
  editingConfig.value = null
  configForm.value = {
    attributeName: '',
    attributeType: 'text',
    inputUnit: '',
    isRequired: 0,
    sortOrder: 0,
    defaultValue: '',
    placeholder: '',
    status: 1,
    categoryId: selectedCategoryId.value
  }
  configDialogVisible.value = true
}

const handleEditConfig = (config) => {
  editingConfig.value = config
  configForm.value = { ...config }
  configDialogVisible.value = true
}

const confirmConfig = async () => {
  try {
    await configFormRef.value.validate()
    
    const payload = { ...configForm.value }
    if (editingConfig.value) {
      payload.id = editingConfig.value.id
    }
    
    await axios.post('/api/meta-category-attribute-config/submit', payload)
    ElMessage.success(editingConfig.value ? '更新成功' : '添加成功')
    configDialogVisible.value = false
    loadAttributeConfigs(selectedCategoryId.value)
  } catch (error) {
    if (error.response?.data?.msg) {
      ElMessage.error(error.response.data.msg)
    } else {
      ElMessage.error('保存失败')
    }
  }
}

const handleDeleteConfig = (config) => {
  ElMessageBox.confirm('删除后不可恢复，是否继续？', '确认删除', { type: 'warning' })
    .then(async () => {
      try {
        await axios.post('/api/meta-category-attribute-config/remove', null, { 
          params: { ids: String(config.id) } 
        })
        ElMessage.success('删除成功')
        loadAttributeConfigs(selectedCategoryId.value)
      } catch (error) {
        ElMessage.error('删除失败')
      }
    })
}

const handleOptionManage = (config) => {
  // 跳转到选项管理页面
  const url = window.location.origin + `/admin/category-attribute-option?configId=${config.id}`
  window.open(url, '_blank')
}

const handleToggleStatus = async (row) => {
  if (!row) return
  const action = row.status === 1 ? '停用' : '启用'
  try {
    await axios.post('/api/common/toggleStatus', null, { params: { entity: 'MetaCategoryAttribute', id: row.id } })
    ElMessage.success(`${action}成功`)
    loadAttributeConfigs(selectedCategoryId.value)
  } catch (e) {
    ElMessage.error(`${action}失败: ${e.response?.data?.msg || e.message}`)
  }
}

const getAttributeTypeLabel = (type) => {
  const labels = {
    text: '文本',
    select: '下拉',
    checkbox: '复选',
    number: '数字',
    date: '日期'
  }
  return labels[type] || type
}

const getAttributeTypeTag = (type) => {
  const tags = {
    text: '',
    select: 'success',
    checkbox: 'warning',
    number: 'info',
    date: 'primary'
  }
  return tags[type] || ''
}

// 生命周期
onMounted(() => {
  loadCategories()
})
</script>

<style scoped>
.category-attribute-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: calc(100vh - 96px);
}

.attribute-card {
  max-width: 1200px;
  margin: 0 auto;
}

.attribute-card :deep(.el-card__body) {
  padding: 24px;
}

.attribute-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.attribute-card-header span {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.category-selector {
  margin-bottom: 24px;
}

.attribute-list {
  margin-top: 16px;
}

.no-data {
  margin-top: 60px;
}
</style>
